import { PrimVoicesProvider, usePrimVoices, WebSocketClientConfig } from "primvoices-react";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { useParams } from "react-router-dom";

import { AgentMonitoring } from "./AgentMonitoring";
import { Button, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { InputField } from "../base/InputField";
import { useAppSelector } from "../../redux";
import { slugify } from "../../utils/strings";
import { AgentEnvironmentDropdownField } from "../AgentEnvironmentDropdownField";
import { API_URL } from "../../config";
import { useApiRequest } from "../../hooks/useApiRequest";
import { PhoneNumber } from "../../redux/reducers/userSlice";

type AgentDebuggerInternalProps = {
  functionId?: string;
  environmentId?: string;
  isOpen: boolean;
  onClick: () => void;
  onChangeEnvironment: (environment: string) => void;
  useCanary: boolean;
  onToggleCanary: () => void;
};

export const AgentDebuggerInternal = ({
  functionId,
  environmentId,
  isOpen,
  onClick,
  onChangeEnvironment,
  useCanary,
  onToggleCanary,
}: AgentDebuggerInternalProps) => {
  const { agentId } = useParams();
  const [isKeyboardActive, setIsKeyboardActive] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [showPhoneDebugger, setShowPhoneDebugger] = useState(false);
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities));
  const agentEnvironment = useAppSelector((state) => (environmentId ? state.agentEnvironment.entities[environmentId] : null));
  const { connect, disconnect, startListening, stopListening, sendTextEvent, isConnected, isListening } = usePrimVoices();
  const configureDebugPhoneNumber = useApiRequest<{ data: PhoneNumber }>();

  const onClickStart = async () => {
    try {
      await disconnect();
      await connect();
    } catch (err) {
      console.error("Failed to start conversation:", err);
    }
  };

  const onClickStop = async () => {
    try {
      await stopListening();
      await disconnect();

      setIsKeyboardActive(false);
      setInputMessage("");
    } catch (err) {
      console.error("Failed to stop conversation:", err);
    }
  };

  const onClickMic = async () => {
    try {
      if (isListening) {
        await stopListening();
      } else {
        await startListening();
      }
    } catch (err) {
      console.error("Failed to toggle microphone:", err);
    }
  };

  const onClickKeyboard = () => {
    setIsKeyboardActive(!isKeyboardActive);
  };

  const onClickSendMessage = async () => {
    if (!inputMessage.trim()) return;

    try {
      // Send message to agent
      const userMessage = inputMessage.trim();
      await sendTextEvent(userMessage);

      // Clear input field
      setInputMessage("");
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onClickSendMessage();
    }
  };

  const onClickPhone = async () => {
    try {
      setShowPhoneDebugger(true);

      await configureDebugPhoneNumber.fetch(`/v1/agents/${agentId}/debugPhoneNumber`, {
        method: "POST",
        body: {
          environment: `${slugify(agentEnvironment?.name || "")}|${functionId}`,
        },
      });
    } catch (err) {
      console.error("Failed to fetch phone number:", err);
    }
  };

  useEffect(() => {
    if (!environmentId && agentEnvironments.length > 0) {
      onChangeEnvironment(agentEnvironments[0].id);
    }
  }, [environmentId, agentEnvironments]);

  useEffect(() => {
    setShowPhoneDebugger(false);
  }, [isOpen, environmentId, functionId]);

  return (
    <View
      className={clsx(
        "relative h-screen bg-white dark:bg-gray-800 shadow-lg border-l border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out",
        isOpen ? "w-full max-w-[min(1000px,48vw)] min-w-[500px]" : "w-[48px]",
        "hidden md:flex flex-col", // Hide on mobile, show on desktop
      )}
    >
      <Button
        type="button"
        variant="ghost"
        className="!absolute p-3 mt-1 !dark:focus:ring-0 !focus:ring-0 self-start hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-10 h-10 flex items-center justify-center ml-1"
        onClick={onClick}
        aria-label={isOpen ? "Collapse debug sidebar" : "Expand debug sidebar"}
      >
        <View className="flex items-center justify-center">
          <Icon
            name={isOpen ? "chevron-right" : "chevron-left"}
            size={20}
          />
        </View>
      </Button>
      <Text className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 text-sm font-medium text-gray-500 dark:text-gray-400 origin-center -rotate-90 transition-opacity duration-300 ease-in-out">
        Debugger
      </Text>
      {isOpen && (
        <View className="ml-12 h-full border-l border-gray-200 dark:border-gray-700 flex-1 max-w-[calc(min(1000px,48vw)-48px)] min-w-[452px]">
          <View className="flex flex-col h-full">
            <View className="flex flex-col gap-2 py-2 px-4 border-b border-gray-200 dark:border-gray-700">
              {/* Environment Selection Row */}
              <View className="flex items-center justify-between">
                <Text className="text-sm font-medium text-gray-600 dark:text-gray-400">Environment</Text>
                <AgentEnvironmentDropdownField
                  agentEnvironments={agentEnvironments}
                  value={environmentId || ""}
                  onChange={onChangeEnvironment}
                  className="w-48 min-w-48 max-w-48 flex-shrink-0"
                />
              </View>

              {/* Main Controls Row */}
              <View className="flex items-center justify-between gap-4">
                <View className="flex items-center gap-4">
                  <View className="flex items-center gap-2">
                    <View className={`w-3 h-3 min-w-3 min-h-3 rounded-full ${isConnected ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                    <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">{isConnected ? "Active" : "Inactive"}</Text>
                  </View>
                  <Button
                    onClick={onToggleCanary}
                    size="icon"
                    className={clsx(
                      "flex items-center !h-8 !w-8 !p-0 rounded-md transition-colors shadow-none",
                      useCanary
                        ? "bg-brand hover:bg-brand-light text-white"
                        : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300",
                    )}
                    title={useCanary ? "Canary: Enabled" : "Canary: Disabled"}
                  >
                    <Icon
                      name="zap"
                      size={16}
                    />
                  </Button>
                </View>

                <View className="flex items-center gap-3">
                  <Button
                    onClick={onClickMic}
                    size="icon"
                    className={clsx(
                      "flex items-center !h-10 !w-10 !p-0 btn-primary rounded-md transition-colors shadow-none bg-brand hover:bg-brand-light flex-shrink-0",
                    )}
                    isDisabled={!isConnected}
                  >
                    <Icon
                      name={isListening ? "mic-off" : "mic"}
                      size={20}
                    />
                  </Button>
                  <Button
                    onClick={onClickKeyboard}
                    size="icon"
                    className={clsx(
                      "flex items-center !h-10 !w-10 !p-0 btn-primary rounded-md transition-colors shadow-none bg-brand hover:bg-brand-light flex-shrink-0",
                    )}
                    isDisabled={!isConnected}
                  >
                    <Icon
                      name={isKeyboardActive ? "keyboard-off" : "keyboard"}
                      size={20}
                    />
                  </Button>
                  <Button
                    onClick={onClickPhone}
                    size="icon"
                    className="flex items-center !h-10 !w-10 !p-0 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors shadow-none flex-shrink-0"
                  >
                    <Icon
                      name="phone"
                      size={20}
                    />
                  </Button>
                  <View className="flex items-center mx-2 h-8 w-[1px] bg-gray-200 dark:bg-gray-700" />
                  <Button
                    onClick={isConnected ? onClickStop : onClickStart}
                    size="icon"
                    className={clsx(
                      "flex items-center gap-1 text-white !h-10 !px-4 !py-2 rounded-md transition-colors !w-auto shadow-none text-sm flex-shrink-0",
                      isConnected
                        ? "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                        : "bg-brand-dark hover:bg-brand dark:bg-brand dark:hover:bg-brand-light",
                    )}
                  >
                    <Icon
                      name={isConnected ? "stop" : "play"}
                      size={16}
                    />
                    {isConnected ? "Stop" : "Start"}
                  </Button>
                </View>
              </View>
            </View>
            {showPhoneDebugger ? (
              <View className="flex flex-col gap-4 p-6">
                {configureDebugPhoneNumber.isLoading ? (
                  <View className="flex flex-col gap-2">
                    <Text
                      variant="muted"
                      className="text-sm"
                    >
                      Configuring debug phone number...
                    </Text>
                  </View>
                ) : configureDebugPhoneNumber.data?.data.phoneNumber ? (
                  <View className="flex flex-col gap-2">
                    <Text
                      variant="muted"
                      className="text-sm"
                    >
                      Call this number to test your agent:
                    </Text>
                    <Text className="text-xl">{configureDebugPhoneNumber.data.data.phoneNumber}</Text>
                  </View>
                ) : (
                  <View className="flex flex-col gap-2">
                    <Text
                      variant="muted"
                      className="text-sm"
                    >
                      No phone number assigned. Please issue a phone number in your profile settings.
                    </Text>
                  </View>
                )}
              </View>
            ) : (
              <View className="flex-1 overflow-y-auto">
                <AgentMonitoring />
              </View>
            )}
            <View
              className={clsx(
                "mt-auto p-4 border-t border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out flex-shrink-0",
                !isKeyboardActive ? "-mb-24" : "mb-0",
              )}
            >
              <View className="relative flex w-full">
                <InputField
                  value={inputMessage}
                  onChange={setInputMessage}
                  placeholder={isConnected ? "Type your message..." : "Start conversation to enable..."}
                  className="flex-1 pr-12 h-14 min-w-0"
                  onKeyDown={onKeyDown}
                  readOnly={!isConnected}
                />
                <View className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <Button
                    onClick={isConnected ? onClickSendMessage : undefined}
                    type="button"
                    variant="ghost"
                    className={`
                  text-white !w-10 !h-10 flex-shrink-0
                  ${
                    !isConnected
                      ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                      : !inputMessage.trim()
                        ? "bg-brand !text-white cursor-not-allowed"
                        : "bg-brand !text-white hover:bg-brand-light"
                  }
                `}
                    isDisabled={!isConnected || !inputMessage.trim()}
                  >
                    <Icon
                      name="send"
                      size={18}
                    />
                  </Button>
                </View>
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

type AgentDebuggerProps = {
  agentId: string;
  functionId: string;
  isOpen: boolean;
  onClick: () => void;
};

export const AgentDebugger = ({ agentId, functionId, isOpen, onClick }: AgentDebuggerProps) => {
  const [environmentId, setEnvironmentId] = useState("");
  const [config, setConfig] = useState<WebSocketClientConfig | undefined>();
  const [useCanary, setUseCanary] = useState(true); // Default checked
  const agentEnvironment = useAppSelector((state) => (environmentId ? state.agentEnvironment.entities[environmentId] : null));

  const onChangeEnvironment = (id: string) => {
    setEnvironmentId(id);
  };

  // Ensure config is set only once or upon certain changes.
  // Passing a new config object on every render can cause issues with the audio worklet.
  useEffect(() => {
    setConfig({
      apiUrl: API_URL,
      agentId,
      environment: `${slugify(agentEnvironment?.name || "")}|${functionId}`,
      logLevel: "INFO",
      canary: useCanary,
      origin: "debugger",
    });
  }, [agentEnvironment, functionId, agentId, useCanary]);

  return config ? (
    <PrimVoicesProvider config={config}>
      <AgentDebuggerInternal
        functionId={functionId}
        environmentId={environmentId}
        isOpen={isOpen}
        onClick={onClick}
        onChangeEnvironment={onChangeEnvironment}
        useCanary={useCanary}
        onToggleCanary={() => setUseCanary(!useCanary)}
      />
    </PrimVoicesProvider>
  ) : null;
};
